import React, { useCallback, useEffect, useMemo, useState } from "react";
import _ from "lodash";
import { Schema } from "types/schema";
import { makeStyles } from "@material-ui/core/styles";
import Immutable, { List, Map } from "immutable";
import { ColDef } from "ag-grid-community";
import { themeOptions } from "styles/theme";
import { IGroupHOCReturnParams } from "components/infinite";
import { ProductCollectionTileView } from "components/product-collection/product-collection-views";
import { ProductCollectionTileViewGroupState } from "components/product-collection/product-collection-views/product-collection-tile-view/product-collection-tile-view-group-state";
import { generateShipmentReviewRowData } from "components/shipment-review/shipment-review-grid";
import { CardView, TileViewSize } from "constants/product-collection";
import {
    buildGridItems,
    constructAllocationDimensions,
    IGridItem,
} from "helpers/legacy-cart/line-items/items-conversion-helper";
import {
    generateViewDefinitionConfig,
    ViewDefinitionConfig,
} from "helpers/legacy-cart/line-items/view-definition-config-helper";
import { schemaToCollectionsSchema } from "helpers/legacy-cart/schema/schema-conversion-helper";
import { IDisplayFields } from "helpers/product-collection";
import { formatMessage } from "lib/globalize-util";
import { safeFormatNumber } from "lib/number";
import Checkbox from "material-ui/Checkbox";
import MenuItem from "material-ui/MenuItem";
import SelectField from "material-ui/SelectField";
import { parseLineItemId } from "components/shipment-review/helpers/buildDeliveryWindowColDef";
import { ITileSettings } from "components/product-collection/manage-tile-view/types";
import { getProductLink, postGotoProduct } from "helpers/legacy-cart/messaging/messaging-helper";
import { IItem, ShipmentReviewModalData } from "components/shipment-review/types";
import { postToTopIframe } from "helpers/window-messages";
import { getInitialState } from "reducers/dimensions-panel/applied/initial-state";
import { delimiter } from "components/infinite/infinite-hocs";
import { SHIPMENT_QUANTITY_MODAL } from "constants/cart";
import { IDateRange, IOrderData } from "types/cart/shipment-review";
import { ICurrency } from "components/product-collection/product-collection-views/product-collection-grid-view/grid-renderers/pinned-row-renderers";
import { IViewDefinition } from "types/view-definitions";
import { getTotalsFromEntries } from "containers/legacy-cart/helpers";
import { formatDateRange } from "components/shipment-review/helpers";

export interface ProductModalData {
    items: IItem[];
    uniqueDates: IDateRange[];
}

interface IProps {
    scope: any;
    orderData: IOrderData;
    productSchema: Schema;
    itemView: IViewDefinition;
    editable?: boolean;
}

interface IComponentDataState {
    cardViewFields: IDisplayFields;
}

interface IRowGroupState {
    option: ROW_GROUP_OPTIONS;
    value: string[];
}

/**
 * Represents the pivot states (selectable).
 */
interface IPivotState {
    products: boolean;
    units: boolean;
    totalRetail: boolean;
    totalCost: boolean;
}

const useStyles = makeStyles({
    containerStyle: {
        display: "flex",
        flexDirection: "row",
        height: "100%",
        "& .productName:hover": {
            color: `${themeOptions.palette.primary1Color} !important`,
            textDecoration: "underline",
        },
    },
    loadingStyle: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
    rightContainer: {
        padding: "15px",
    },
    rightSectionLabel: {
        fontSize: "16px",
        fontWeight: "bold",
    },
    rightSectionContainer: { marginTop: "30px" },
    groupbyLabel: { fontSize: "14px", fontWeight: "bold" },
});

export const collectDataForLineItemModal = (
    shipmentReviewRowData: IItem[],
    lineItemId: string,
    uniqueDates: IDateRange[],
): ProductModalData => ({
    items: [...shipmentReviewRowData.filter((element) => parseLineItemId(element.item.id) === lineItemId)],
    uniqueDates,
});

export const groupLineItemsProductsById = (shipmentReviewRowData: IItem[], lineItemId: string) => {
    const row = _.find(
        shipmentReviewRowData,
        (rowDataItem) => _.get(rowDataItem, "item.cartLineItemId") === lineItemId,
    );
    const groupProductId = row?.item?.product?._id;
    return groupProductId
        ? _.reduce(
              shipmentReviewRowData,
              (acc, rowDataItem) => {
                  const cartLineItemId = _.get(rowDataItem, "item.cartLineItemId");
                  if (cartLineItemId && rowDataItem?.item?.product?._id === groupProductId) {
                      acc[cartLineItemId] = acc[cartLineItemId] ? [...acc[cartLineItemId], rowDataItem] : [rowDataItem];
                  }
                  return acc;
              },
              {},
          )
        : [row];
};

export const getLineItemsModalData = (
    shipmentReviewRowData: IItem[],
    lineItemId: string,
    uniqueDates: IDateRange[],
): Partial<ShipmentReviewModalData> =>
    _.reduce(
        _.keys(groupLineItemsProductsById(shipmentReviewRowData, lineItemId)),
        (acc, cartLineItemId) => {
            const { items } = collectDataForLineItemModal(shipmentReviewRowData, cartLineItemId, uniqueDates);
            return {
                ...acc,
                items: [...acc.items, ...items],
            };
        },
        { uniqueDates, items: [] },
    );

export const TileViewContainer: React.FunctionComponent<IProps> = ({
    orderData,
    scope,
    productSchema,
    itemView,
    editable,
}) => {
    const [componentData, setComponentData] = React.useState<IComponentDataState>({
        cardViewFields: {},
    });
    const [selectedRowGroup, setSelectedRowGroup] = React.useState<IRowGroupState>({ option: undefined, value: [] });
    const [selectedPivot, setSelectedPivot] = React.useState<IPivotState>({
        products: true,
        units: true,
        totalCost: true,
        totalRetail: true,
    });
    const [sizingDialogParams, setSizingDialogParams] = useState<{ id: string; refresh?: boolean }>(null);

    const tileViewSettings = useMemo<ViewDefinitionConfig>(
        () => ({
            showUnits: true,
            showDoors: false,
            showTotalCost: true,
            costLabel: formatMessage("Total Wholesale"),
            totalCostLabel: formatMessage("Total Wholesale"),
            showTotalRetail: true,
            retailLabel: formatMessage("Total MSRP"),
            totalRetailLabel: formatMessage("Total MSRP"),
            showEachesOnHover: false,
            ...generateViewDefinitionConfig(itemView),
        }),
        [itemView],
    );

    const items = useMemo<List<IItem>>(
        () =>
            Immutable.fromJS(
                buildGridItems(orderData?.entries, {
                    productSchema,
                    productSplit: true,
                    largeView: true,
                    currency: orderData?.currency,
                }),
            ),
        [orderData?.currency, orderData?.entries, productSchema],
    );
    const allocDimensions = useMemo(
        () => constructAllocationDimensions(orderData?.uniqueDates),
        [orderData?.uniqueDates],
    );
    const currency = useMemo<ICurrency>(
        () => ({
            symbol: orderData?.currency.wholesale.symbol,
            currency_code: orderData?.currency.wholesale.code,
            decimal_places: orderData?.currency.wholesale.decimalPlaces,
        }),
        [orderData?.currency],
    );

    const schema = useMemo<ColDef[]>(
        () =>
            schemaToCollectionsSchema(productSchema, itemView, {
                currency: orderData?.currency,
            }),
        [productSchema, itemView, orderData?.currency],
    );

    // TODO: move to the ProductItemModal
    const updateDialogData = useCallback(
        (lineItemId = sizingDialogParams?.id, open?: boolean) => {
            const shipmentReviewRowData = generateShipmentReviewRowData(orderData, itemView, productSchema);
            postToTopIframe({
                action: SHIPMENT_QUANTITY_MODAL,
                payload: {
                    open: open || undefined,
                    data: {
                        ...getLineItemsModalData(shipmentReviewRowData, lineItemId, orderData?.uniqueDates),
                        currencyCode: orderData?.currency?.wholesale?.code,
                    } as ShipmentReviewModalData,
                },
            });
        },
        [orderData, sizingDialogParams, itemView, productSchema],
    );

    useEffect(() => {
        if (sizingDialogParams) {
            updateDialogData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderData?.entries]);

    const openSizingGrid = React.useCallback(
        (lineItemId) => {
            updateDialogData(lineItemId, true);
            setSizingDialogParams({ id: lineItemId });
        },
        [updateDialogData],
    );
    const classes = useStyles();

    React.useEffect(() => {
        if (orderData?.entries) {
            // set card view fields. This is used by product-cart-tile in computing currency.
            const cardViewFields = {};
            CardView.FIELDS.forEach((field) => {
                if (field === "is_retail" || field === "is_cost") {
                    cardViewFields[field] = {
                        field: Immutable.fromJS({
                            number_format: {
                                currency: {
                                    symbol: currency?.symbol,
                                },
                            },
                        }),
                        path: [],
                    };
                }
            });

            setComponentData({ cardViewFields });
            setSelectedRowGroup({ option: ROW_GROUP_OPTIONS.NONE, value: [] });
        }
    }, [orderData?.entries, currency]);

    const handleGrouping = useCallback(
        (e, index, value: ROW_GROUP_OPTIONS) => {
            let rowGroupValue = [];
            switch (value) {
                case ROW_GROUP_OPTIONS.NONE: {
                    rowGroupValue = [];
                    // reset pivot options
                    setSelectedPivot({ products: true, units: true, totalCost: true, totalRetail: true });
                    break;
                }
                case ROW_GROUP_OPTIONS.DELIVERY: {
                    rowGroupValue = [allocDimensions[0]?.id];
                    break;
                }
                case ROW_GROUP_OPTIONS.COLOR: {
                    // prevent use color filter when it's disabled by view definition
                    if (!tileViewSettings?.showColorSwatch) {
                        return;
                    }
                    rowGroupValue = [value];
                    break;
                }
                default: {
                    rowGroupValue = [value];
                }
            }
            setSelectedRowGroup({ option: value, value: rowGroupValue });
        },
        [allocDimensions, tileViewSettings?.showColorSwatch],
    );

    const handlePivotCompute = (e, pivotOption) => {
        const currentPivotState = selectedPivot;
        setSelectedPivot({ ...currentPivotState, [pivotOption]: e.target.checked });
    };

    /**
     * Will be removed in NU-62782
     * Override behavior for grouping of items by delivery dimension.
     * The default grouping logic from group-hoc.tsx component will not work since we don't have breadcrumbs in the items.
     * The breadcrumbs is a property assigned to items json (done via getViewDataFromApi in collection.ts) and is applicable only when aggrid context
     * is available. Tile view doesn't have aggrid context yet.
     * @param items
     * @returns
     */
    const groupByDeliveryDimension = useCallback((items: List<IGridItem>): IGroupHOCReturnParams => {
        const ungroupedItems = _.flatMap(items.toJS(), (item: IGridItem) =>
            _.map(
                _.groupBy(item.entries, (entry) => formatDateRange(entry.shipStart, entry.shipEnd)),
                (entries, shipment) => ({ ...item, entries, shipment }),
            ),
        );

        const groupedItems = Immutable.fromJS(ungroupedItems)
            .groupBy((item) => item.get("shipment") || "N/A")
            .mapEntries(([key, items]) => [key, Map({ groupKeyValues: List([key]), items })]);

        return { items, groupedItems: groupedItems, isGrouped: true, groupDirection: "desc" };
    }, []);

    const computeGroupAndPivotData = (items: List<IGridItem>, groupKeyValues: List<string>) => {
        const totals = _.reduce(
            items.toJS(),
            (totals, item: IGridItem) => {
                const itemTotals = getTotalsFromEntries(item.entries);
                return {
                    units: totals.units + itemTotals.units,
                    wholesale: totals.wholesale + itemTotals.wholesale,
                    retail: totals.retail + itemTotals.retail,
                };
            },
            {
                units: 0,
                wholesale: 0,
                retail: 0,
            },
        );

        const stats = _.compact([
            selectedPivot.products && { name: formatMessage("Products"), value: items.size },
            selectedPivot.units && { name: formatMessage("Units"), value: totals.units },
            selectedPivot.totalCost && {
                name: tileViewSettings.totalCostLabel,
                value: currency.symbol + " " + safeFormatNumber(totals.wholesale),
            },
            selectedPivot.totalRetail && {
                name: tileViewSettings.totalRetailLabel,
                value: currency.symbol + " " + safeFormatNumber(totals.retail),
            },
        ]);

        return (
            <div className="groupHeader">
                <div className="groupTitle">{groupKeyValues?.join(delimiter) || "N/A"}</div>
                <div className="groupStats">
                    {stats.map((stat) => (
                        <ProductCollectionTileViewGroupState key={stat.name} name={stat.name} value={stat.value} />
                    ))}
                </div>
            </div>
        );
    };

    const getProductNameLink = useCallback(
        (productId: string) => getProductLink(scope.brand._id, productId),
        [scope.brand._id],
    );
    const onClickProductName = useCallback(
        (productId: string) => postGotoProduct(scope.brand._id, productId),
        [scope.brand._id],
    );

    const immutableSchema = useMemo(() => Immutable.fromJS(schema), [schema]); // TODO: added for consistency

    return (
        <div className={classes.containerStyle}>
            <ProductCollectionTileView
                rawItemDisplayProperty={schema}
                schema={immutableSchema}
                cardViewFields={componentData.cardViewFields}
                merchantCurrency={currency}
                allocationDimensions={allocDimensions}
                rowGroup={selectedRowGroup.value}
                groupItemsOverride={
                    selectedRowGroup.option === ROW_GROUP_OPTIONS.DELIVERY ? groupByDeliveryDimension : undefined
                }
                tileSettings={tileViewSettings as ITileSettings}
                openSizingGrid={editable ? openSizingGrid : undefined}
                onClickProductName={onClickProductName}
                getProductNameLink={getProductNameLink}
                // stubs
                aggregationData={Map<string, any>()}
                allowDuplicateProduct={undefined}
                allowedValueFields={{}}
                brandEntities={undefined}
                bulkAddingItems={false}
                collectionId={undefined}
                dataEntryType={undefined}
                dimensionFilters={[]}
                doors={Immutable.fromJS([])}
                dimensionsPanelApplied={getInitialState()}
                editable={false}
                filterAssortmentByBrand={undefined}
                filterAssortmentBySMU={undefined}
                formulaFields={{}}
                gridApi={{}}
                gridParams={{}}
                groupHeaders={[]}
                groupRendererOverride={({ items, groupKeyValues }) => computeGroupAndPivotData(items, groupKeyValues)}
                items={items}
                snapshotItems={items}
                itemsId={undefined}
                onLinkProductToItem={_.noop}
                onMoveItem={_.noop}
                onOpenProductModal={_.noop}
                onOpenDeleteConfirmation={_.noop}
                onAddItem={_.noop}
                searchableFields={[[]]}
                tileViewSize={TileViewSize.Medium}
            />
            <div className={classes.rightContainer}>
                <div>
                    <span className={classes.rightSectionLabel}>{formatMessage("Grouping")}</span>
                </div>
                <hr />
                <div className={classes.rightSectionContainer}>
                    <span className={classes.groupbyLabel}>{formatMessage("Group by")}</span>
                    <div>
                        <SelectField
                            id="tile-view-group-by-select"
                            value={selectedRowGroup.option}
                            onChange={handleGrouping}
                        >
                            <MenuItem
                                key={"none"}
                                value={ROW_GROUP_OPTIONS.NONE}
                                primaryText={formatMessage("No grouping")}
                            />
                            <MenuItem
                                key={"delivery"}
                                value={ROW_GROUP_OPTIONS.DELIVERY}
                                primaryText={formatMessage("Delivery")}
                            />
                            <MenuItem
                                key={"color"}
                                disabled={!tileViewSettings?.showColorSwatch}
                                value={ROW_GROUP_OPTIONS.COLOR}
                                primaryText={formatMessage("Color")}
                            />
                            <MenuItem
                                key={"category"}
                                value={ROW_GROUP_OPTIONS.CATEGORY}
                                primaryText={formatMessage("Category")}
                            />
                        </SelectField>
                    </div>
                </div>
                <div className={classes.rightSectionContainer}>
                    <div>
                        <Checkbox
                            label={formatMessage("Products")}
                            checked={selectedPivot.products}
                            disabled={selectedRowGroup.option === ROW_GROUP_OPTIONS.NONE}
                            onCheck={(e) => handlePivotCompute(e, "products")}
                        />
                        <Checkbox
                            label={formatMessage("Units")}
                            disabled={selectedRowGroup.option === ROW_GROUP_OPTIONS.NONE}
                            checked={selectedPivot.units}
                            onCheck={(e) => handlePivotCompute(e, "units")}
                        />
                        <Checkbox
                            label={tileViewSettings.totalCostLabel}
                            checked={selectedPivot.totalCost}
                            disabled={selectedRowGroup.option === ROW_GROUP_OPTIONS.NONE}
                            onCheck={(e) => handlePivotCompute(e, "totalCost")}
                        />
                        <Checkbox
                            label={tileViewSettings.totalRetailLabel}
                            checked={selectedPivot.totalRetail}
                            disabled={selectedRowGroup.option === ROW_GROUP_OPTIONS.NONE}
                            onCheck={(e) => handlePivotCompute(e, "totalRetail")}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

enum ROW_GROUP_OPTIONS {
    NONE = "none",
    COLOR = "color",
    DELIVERY = "delivery",
    CATEGORY = "category",
}
